import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PaginationComponent from './PaginationComponent';
import { useI18n } from '../hooks/useI18n';

interface PaginationData {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

interface PaginationFooterProps {
    pagination: PaginationData;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    pageSizeOptions?: number[];
    className?: string;
}

export default function PaginationFooter({
    pagination,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
    className = ""
}: PaginationFooterProps) {
    const { t } = useI18n();
    const handlePageSizeChange = (value: string) => {
        onPageSizeChange(parseInt(value));
    };

    return (
        <div className={`mt-6  ${className}`}>
            <div className="space-y-4">
                {/* Records info and page size selector */}
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        {pagination.total > 0 ? (
                            <>
                                {t('pagination.showingResults', 'common', {
                                    from: ((pagination.page - 1) * pagination.limit) + 1,
                                    to: Math.min(pagination.page * pagination.limit, pagination.total),
                                    total: pagination.total
                                })}
                            </>
                        ) : (
                            <>{t('pagination.noResults', 'common')}</>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Label htmlFor="page-size" className="text-sm">{t('pagination.show', 'common')}</Label>
                        <Select
                            value={pagination.limit.toString()}
                            onValueChange={handlePageSizeChange}
                        >
                            <SelectTrigger className="w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {pageSizeOptions.map((size) => (
                                    <SelectItem key={size} value={size.toString()}>
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Pagination component - only show if more than 1 page */}
                {pagination.pages > 1 && (
                    <PaginationComponent
                        currentPage={pagination.page}
                        totalPages={pagination.pages}
                        onPageChange={onPageChange}
                    />
                )}
            </div>
        </div>
    );
}
